import React, { useState, useEffect } from 'react';
import { Box, Text, useColorModeValue, Checkbox, Button, HStack, Input, InputGroup, InputLeftElement, Table, Thead, Tbody, Tr, Th, Td, Badge, VStack, Card, CardBody, SimpleGrid, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Divider, Tag, TagLabel, Progress } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import UploadPopulation from './UploadPopulation';
import CollapsibleSection from './shared/CollapsibleSection';
import LoadingSpinner from './shared/LoadingSpinner';
import ErrorMessage from './shared/ErrorMessage';
import EmptyState from './shared/EmptyState';
import Breadcrumbs from './shared/Breadcrumbs';
import { useConfirmDialog } from './shared/ConfirmDialog';
import { useToast } from '../hooks/useToast';
import fetchWithAuth from '../utils/fetchWithAuth';

function PopulationsList() {
    const [populations, setPopulations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPopulations, setShowPopulations] = useState(true);
    const [selectedPopulations, setSelectedPopulations] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [demographicData, setDemographicData] = useState({});

    const bgColor = useColorModeValue('background.shade1');
    const color = useColorModeValue('theme.black');

    const { confirm, ConfirmDialogComponent } = useConfirmDialog();
    const toast = useToast();

    const handleSelectPopulation = (popId) => {
        const newSelected = new Set(selectedPopulations);
        if (newSelected.has(popId)) {
            newSelected.delete(popId);
        } else {
            newSelected.add(popId);
        }
        setSelectedPopulations(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedPopulations.size === populations.length) {
            setSelectedPopulations(new Set());
        } else {
            setSelectedPopulations(new Set(populations.map(p => p.id)));
        }
    };

    const handleBulkDelete = () => {
        confirm({
            title: 'Delete Selected Populations',
            message: `Are you sure you want to delete ${selectedPopulations.size} population(s)? This will also delete all associated individuals.`,
            confirmText: 'Delete All',
            onConfirm: async () => {
                try {
                    const deletePromises = Array.from(selectedPopulations).map(popId =>
                        fetchWithAuth(`/api/populations/${popId}/`, { method: 'DELETE' })
                    );

                    await Promise.all(deletePromises);

                    setPopulations(populations.filter(pop => !selectedPopulations.has(pop.id)));
                    setSelectedPopulations(new Set());
                    toast.showSuccess('Success', `${selectedPopulations.size} population(s) deleted`);
                } catch (error) {
                    toast.showError('Error', 'Failed to delete some populations');
                }
            }
        });
    };

    useEffect(() => {
        fetchPopulations();
    }, []);

    const fetchPopulations = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth('/api/populations/', {
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const pops = data.results || [];
            setPopulations(pops);
            setError(null);
        } catch (error) {
            setError('Failed to fetch populations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const togglePopulations = () => {
        setShowPopulations(!showPopulations);
    };

    const fetchDemographicData = async (populationId) => {
        if (demographicData[populationId]) {
            // Already fetched
            return;
        }

        try {
            const response = await fetchWithAuth(`/api/populations/${populationId}/demographics/`);
            const data = await response.json();
            setDemographicData(prev => ({
                ...prev,
                [populationId]: data
            }));
        } catch (error) {
            toast.showError('Error', 'Failed to fetch demographic data');
        }
    };

    const handleAccordionChange = (populationId) => {
        if (!demographicData[populationId]) {
            fetchDemographicData(populationId);
        }
    };

    const filteredPopulations = populations.filter(pop =>
        pop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (pop.description && pop.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <Box bg={bgColor} color={color} minHeight="100vh">
            <ConfirmDialogComponent />
            <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
                <Breadcrumbs
                    items={[
                        { label: 'Home', path: '/' },
                        { label: 'Populations' }
                    ]}
                />

                <CollapsibleSection
                    title="View Populations"
                    isOpen={showPopulations}
                    onToggle={togglePopulations}
                >
                {populations.length === 0 ? (
                    <EmptyState message="No populations available." />
                ) : (
                    <>
                        <InputGroup mb={4}>
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.300" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search populations by name or description..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </InputGroup>
                        <HStack spacing={4} mb={4}>
                            <Checkbox
                                isChecked={selectedPopulations.size === filteredPopulations.length && filteredPopulations.length > 0}
                                onChange={handleSelectAll}
                            >
                                Select All
                            </Checkbox>
                            {selectedPopulations.size > 0 && (
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={handleBulkDelete}
                                >
                                    Delete Selected ({selectedPopulations.size})
                                </Button>
                            )}
                        </HStack>
                        {filteredPopulations.length === 0 ? (
                            <EmptyState message="No populations match your search." />
                        ) : (
                            <Accordion allowMultiple>
                                {filteredPopulations.map(population => {
                                    const demo = demographicData[population.id];
                                    return (
                                        <AccordionItem key={population.id} border="1px solid" borderColor="gray.300" borderRadius="md" mb={3}>
                                            <h2>
                                                <AccordionButton onClick={() => handleAccordionChange(population.id)} p={4}>
                                                    <HStack flex="1" spacing={4} align="center">
                                                        <Checkbox
                                                            isChecked={selectedPopulations.has(population.id)}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectPopulation(population.id);
                                                            }}
                                                        />
                                                        <VStack align="start" flex="1" spacing={1}>
                                                            <Text fontWeight="bold" fontSize="lg">{population.name}</Text>
                                                            {population.description && (
                                                                <Text fontSize="sm" color="gray.600">{population.description}</Text>
                                                            )}
                                                        </VStack>
                                                        <Badge colorScheme={population.num_individuals > 0 ? 'green' : 'gray'} fontSize="md">
                                                            {population.num_individuals?.toLocaleString() || 0} individuals
                                                        </Badge>
                                                    </HStack>
                                                    <AccordionIcon ml={2} />
                                                </AccordionButton>
                                            </h2>
                                            <AccordionPanel pb={4} bg="background.shade2">
                                                {!demo ? (
                                                    <LoadingSpinner />
                                                ) : (
                                                    <VStack align="stretch" spacing={4}>
                                                        {/* Column Headers */}
                                                        <Box>
                                                            <Text fontWeight="bold" mb={2}>Available Columns:</Text>
                                                            <HStack flexWrap="wrap" spacing={2}>
                                                                {demo.columns.map(col => (
                                                                    <Tag key={col} size="md" colorScheme="blue">
                                                                        <TagLabel>{col}</TagLabel>
                                                                    </Tag>
                                                                ))}
                                                            </HStack>
                                                        </Box>

                                                        <Divider />

                                                        {/* Demographic Breakdowns */}
                                                        <Box>
                                                            <Text fontWeight="bold" mb={3}>Demographic Breakdowns:</Text>
                                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                                {Object.entries(demo.demographic_breakdowns).map(([column, data]) => (
                                                                    <Card key={column} size="sm">
                                                                        <CardBody>
                                                                            <Text fontWeight="semibold" mb={2}>{column}</Text>
                                                                            <VStack align="stretch" spacing={1}>
                                                                                {data.values.slice(0, 5).map((item, idx) => (
                                                                                    <Box key={idx}>
                                                                                        <HStack justify="space-between">
                                                                                            <Text fontSize="sm">{item.value}</Text>
                                                                                            <Badge>{item.percentage}%</Badge>
                                                                                        </HStack>
                                                                                        <Progress value={item.percentage} size="xs" colorScheme="blue" mt={1} />
                                                                                    </Box>
                                                                                ))}
                                                                                {data.total_unique > 5 && (
                                                                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                                                                        +{data.total_unique - 5} more values
                                                                                    </Text>
                                                                                )}
                                                                            </VStack>
                                                                        </CardBody>
                                                                    </Card>
                                                                ))}
                                                            </SimpleGrid>
                                                        </Box>

                                                        <Divider />

                                                        {/* Sample Data */}
                                                        <Box>
                                                            <Text fontWeight="bold" mb={2}>Sample Data (first 5 individuals):</Text>
                                                            <Box overflowX="auto">
                                                                <Table size="sm" variant="simple">
                                                                    <Thead>
                                                                        <Tr>
                                                                            <Th>Phone</Th>
                                                                            {demo.columns.map(col => (
                                                                                <Th key={col}>{col}</Th>
                                                                            ))}
                                                                        </Tr>
                                                                    </Thead>
                                                                    <Tbody>
                                                                        {demo.sample_data.map((individual, idx) => (
                                                                            <Tr key={idx}>
                                                                                <Td fontSize="sm">{individual.phone_number}</Td>
                                                                                {demo.columns.map(col => (
                                                                                    <Td key={col} fontSize="sm">
                                                                                        {individual.demographic_data[col] !== undefined
                                                                                            ? String(individual.demographic_data[col])
                                                                                            : '-'}
                                                                                    </Td>
                                                                                ))}
                                                                            </Tr>
                                                                        ))}
                                                                    </Tbody>
                                                                </Table>
                                                            </Box>
                                                        </Box>
                                                    </VStack>
                                                )}
                                            </AccordionPanel>
                                        </AccordionItem>
                                    );
                                })}
                            </Accordion>
                        )}
                    </>
                )}
                </CollapsibleSection>
                <Box mt={4}>
                    <UploadPopulation onUploadSuccess={fetchPopulations} />
                </Box>
            </Box>
        </Box>
    );
}

export default PopulationsList;
